import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, FileText, Eye, Trash2, Loader2, Download } from "lucide-react";
import { Button } from "./ui/button";
import { Progress } from "./ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { uploadDocument, listDocuments, downloadDocument, deleteDocument } from "../lib/api";

const DOC_TYPES = [
  { value: "pan", label: "PAN Card" },
  { value: "aadhaar", label: "Aadhaar Card" },
  { value: "income_proof", label: "Income Proof" },
  { value: "bank_statement", label: "Bank Statement" },
  { value: "address_proof", label: "Address Proof" },
  { value: "other", label: "Other" },
];
const TYPE_LABEL = Object.fromEntries(DOC_TYPES.map(d => [d.value, d.label]));
const ACCEPT = ".pdf,.jpg,.jpeg,.png,.webp";
const fmtSize = (b) => (b > 1024 * 1024 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);
const fmtDate = (s) => { try { return new Date(s).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }); } catch { return "—"; } };

export const DocumentsManager = () => {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [docType, setDocType] = useState("pan");
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [busyId, setBusyId] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => { listDocuments().then(setDocs).catch(() => setDocs([])).finally(() => setLoading(false)); }, []);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("File too large (max 10 MB)"); e.target.value = ""; return; }
    const fd = new FormData();
    fd.append("file", file);
    fd.append("doc_type", docType);
    setUploading(true); setProgress(0);
    try {
      const doc = await uploadDocument(fd, (ev) => {
        if (ev.total) setProgress(Math.round((ev.loaded / ev.total) * 100));
      });
      setDocs((d) => [doc, ...d]);
      toast.success("Document uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false); setProgress(0);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const onView = async (doc) => {
    setBusyId(doc.id);
    try {
      const blob = await downloadDocument(doc.id);
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      toast.error("Could not open document");
    } finally {
      setBusyId(null);
    }
  };

  const onDownload = async (doc) => {
    setBusyId(doc.id);
    try {
      const blob = await downloadDocument(doc.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.original_filename || "document";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success("Download started");
    } catch {
      toast.error("Could not download document");
    } finally {
      setBusyId(null);
    }
  };

  const onDelete = async (doc) => {
    setBusyId(doc.id);
    try {
      await deleteDocument(doc.id);
      setDocs((d) => d.filter((x) => x.id !== doc.id));
      toast.success("Document removed");
    } catch {
      toast.error("Could not delete document");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div data-testid="documents-manager" className="mt-5 bg-white border border-[#E2E8F0] rounded-2xl p-6 max-w-3xl">
      <div className="flex items-center gap-3">
        <Upload size={18} className="text-[#1088D4]" />
        <div>
          <p className="text-sm font-semibold text-[#273B59]">Documents</p>
          <p className="text-xs text-[#94A3B8]">Upload PAN, Aadhaar, income & address proof (PDF/JPG/PNG, max 10 MB).</p>
        </div>
      </div>

      <div className="mt-5 flex flex-col sm:flex-row gap-3 sm:items-end">
        <div className="sm:w-52">
          <label className="text-xs font-medium text-[#475569] mb-1.5 block">Document Type</label>
          <Select value={docType} onValueChange={setDocType}>
            <SelectTrigger data-testid="doc-type-select" className="bg-white"><SelectValue /></SelectTrigger>
            <SelectContent>
              {DOC_TYPES.map(d => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <input ref={inputRef} data-testid="doc-file-input" type="file" accept={ACCEPT} onChange={onFile} className="hidden" />
        <Button data-testid="doc-upload-btn" type="button" disabled={uploading} onClick={() => inputRef.current?.click()} className="ol-btn-primary h-10">
          {uploading ? <Loader2 className="animate-spin mr-2" size={15} /> : <Upload size={15} className="mr-2" />}
          {uploading ? "Uploading…" : "Choose & Upload"}
        </Button>
      </div>

      {uploading && <Progress data-testid="doc-progress" value={progress} className="mt-4 h-2" />}

      <div className="mt-6" data-testid="documents-list">
        {loading ? (
          <div className="py-6 flex justify-center"><Loader2 className="animate-spin text-[#1088D4]" size={22} /></div>
        ) : docs.length === 0 ? (
          <p data-testid="docs-empty" className="text-sm text-[#94A3B8] py-4 text-center">No documents uploaded yet.</p>
        ) : (
          <div className="space-y-2">
            {docs.map((d) => (
              <div key={d.id} data-testid={`doc-row-${d.id}`} className="flex items-center gap-3 border border-[#E2E8F0] rounded-xl p-3">
                <div className="w-9 h-9 rounded-lg bg-[#EAF5FD] flex items-center justify-center shrink-0"><FileText size={16} className="text-[#1088D4]" /></div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-[#273B59] truncate">{d.original_filename}</p>
                  <p className="text-xs text-[#94A3B8]">{TYPE_LABEL[d.doc_type] || d.doc_type} · {fmtSize(d.size)} · {fmtDate(d.created_at)}</p>
                </div>
                <Button data-testid={`doc-view-${d.id}`} size="icon" variant="ghost" disabled={busyId === d.id} onClick={() => onView(d)} className="text-[#1088D4]"><Eye size={16} /></Button>
                <Button data-testid={`doc-download-${d.id}`} size="icon" variant="ghost" disabled={busyId === d.id} onClick={() => onDownload(d)} className="text-[#273B59]"><Download size={16} /></Button>
                <Button data-testid={`doc-delete-${d.id}`} size="icon" variant="ghost" disabled={busyId === d.id} onClick={() => onDelete(d)} className="text-red-600"><Trash2 size={16} /></Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
